/**
 * Shared "Copy to Clipboard" and "Copy for WhatsApp" action row used by every
 * macro component (Puck + legacy).
 *
 * - "Copy to Clipboard" writes the macro's full HTML to the clipboard so a
 *   desktop user can paste rich text into Zendesk/email.
 * - "Copy for WhatsApp" writes WhatsApp-formatted plain text (markers like
 *   *bold*, _italic_, real newlines) so a mobile user can paste straight into
 *   a chat thread.
 */

import React, { useCallback } from 'react';
import { htmlToWhatsApp } from '../../MacroPuck/htmlToWhatsApp';
import { MacroFeedbackBanner, useMacroFeedback } from './useMacroFeedback';

export interface MacroCopyButtonsProps {
    /** Return the macro's current rendered HTML, or null if not ready yet. */
    getHtml: () => string | null;
    /**
     * Optional plain-text variant used when writing the rich-clipboard fallback.
     * Only Puck components have a direct DOM ref offering this; legacy ones can
     * omit it and we'll derive plain text from the HTML.
     */
    getText?: () => string | null;
}

const BUTTON_STYLE_BASE: React.CSSProperties = {
    padding: '10px 18px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '40px',
};

export function MacroCopyButtons({ getHtml, getText }: MacroCopyButtonsProps) {
    const { feedback, showSuccess, showError, dismiss } = useMacroFeedback();

    const copyHtml = useCallback(async () => {
        const html = getHtml();
        if (!html) {
            showError('Macro is not ready yet.');
            return;
        }
        try {
            const text = getText?.() ?? html;
            if (typeof ClipboardItem !== 'undefined') {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([text], { type: 'text/plain' }),
                    }),
                ]);
            } else {
                await navigator.clipboard.writeText(html);
            }
            showSuccess('Copied to clipboard.');
        } catch (err) {
            try {
                await navigator.clipboard.writeText(getText?.() ?? html);
                showSuccess('Copied as text.');
            } catch {
                console.error('Failed to copy content to clipboard:', err);
                showError('Failed to copy. Please try again.');
            }
        }
    }, [getHtml, getText, showSuccess, showError]);

    const copyForWhatsApp = useCallback(async () => {
        const html = getHtml();
        if (!html) {
            showError('Macro is not ready yet.');
            return;
        }
        try {
            const text = htmlToWhatsApp(html);
            await navigator.clipboard.writeText(text);
            showSuccess('Copied for WhatsApp. Paste into your chat.');
        } catch (err) {
            console.error('Failed to copy WhatsApp text to clipboard:', err);
            showError('Failed to copy. Please try again.');
        }
    }, [getHtml, showSuccess, showError]);

    return (
        <div className="flex flex-col gap-2 items-stretch sm:items-end">
            <div className="flex flex-wrap justify-end gap-2">
                <button
                    type="button"
                    onClick={copyHtml}
                    style={{ ...BUTTON_STYLE_BASE, backgroundColor: '#005bbb' }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#004a99')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#005bbb')}
                >
                    📋 Copy to Clipboard
                </button>
                <button
                    type="button"
                    onClick={copyForWhatsApp}
                    style={{ ...BUTTON_STYLE_BASE, backgroundColor: '#116b35' }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0c5429')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#116b35')}
                >
                    💬 Copy for WhatsApp
                </button>
            </div>
            <MacroFeedbackBanner feedback={feedback} onDismiss={dismiss} />
        </div>
    );
}
